import React, { useEffect, useState } from "react";
import api, { formatPHP, formatErrorDetail } from "@/lib/api";
import { Surface, TableWrap, Empty } from "@/components/app/Primitives";
import EmployeePicker from "@/components/app/EmployeePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Check } from "lucide-react";

// Mirrors services/typed_pay.py (EARNING_KINDS / DEDUCTION_KINDS). The server refuses anything else.
const EARNING_TYPES = [["bonus", "Bonus"], ["commission", "Commission"], ["14th_month", "14th month pay"], ["performance", "Performance bonus"], ["christmas", "Christmas bonus"], ["anniversary", "Anniversary bonus"], ["referral", "Referral bonus"], ["attendance_incentive", "Attendance incentive"], ["incentive", "Incentive"], ["manual", "Other earning"]];
const DEDUCTION_TYPES = [["uniform", "Uniform"], ["equipment", "Equipment"], ["cash_advance", "Cash advance"], ["damages", "Damages or shortages"], ["other", "Other deduction"], ["manual", "Manual deduction"]];

const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const monthEnd = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10); };
const blank = () => ({ employee_id: "", kind: "deduction", adjustment_type: "uniform", label: "", amount: "", period_start: monthStart(), period_end: monthEnd() });

/** One-time deductions (uniform, equipment, cash advance...) and one-time earnings a payroll officer records.
 *  They are taken on the Basic run whose cutoff overlaps the period, once approved. */
export default function PayrollAdjustments({ canEdit }) {
    const [items, setItems] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(blank());
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/payroll/adjustments").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    useEffect(() => { load(); }, []);

    const types = form.kind === "earning" ? EARNING_TYPES : DEDUCTION_TYPES;
    const save = async () => {
        if (!form.employee_id) return toast.error("Choose the employee");
        if (!(Number(form.amount) > 0)) return toast.error("Enter the amount");
        setBusy(true);
        try {
            await api.post("/payroll/adjustments", { ...form, amount: Number(form.amount) });
            toast.success("Recorded. Approve it so the next Basic run takes it");
            setOpen(false); setForm(blank()); load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const approve = async (a) => {
        try { await api.post(`/payroll/adjustments/${a.id}/approve`); toast.success("Approved"); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };
    const typeLabel = (a) => [...EARNING_TYPES, ...DEDUCTION_TYPES].find(([k]) => k === a.adjustment_type)?.[1] || a.adjustment_type || "Adjustment";

    return (
        <div className="mt-8" data-testid="payroll-adjustments">
            <div className="flex items-center justify-between mb-2">
                <div>
                    <h3 className="font-semibold">One-time deductions and earnings</h3>
                    <p className="text-xs text-muted-foreground">Uniform, equipment, a cash advance repaid in one go, a referral reward. Taken once on the Basic run whose cutoff overlaps the period, after approval. Repaid in instalments? Use a loan above.</p>
                </div>
                {canEdit && (
                    <button className="h-9 px-3 rounded-md border border-border text-sm flex items-center gap-2" onClick={() => setOpen(true)} data-testid="new-adjustment-btn"><Plus className="size-4" /> Record one</button>
                )}
            </div>
            {items && !items.length && <Surface className="p-4"><Empty title="Nothing recorded" /></Surface>}
            {items && items.length > 0 && (
                <TableWrap>
                    <thead><tr><th>Type</th><th>Label</th><th>Period</th><th className="num">Amount</th><th>Status</th><th /></tr></thead>
                    <tbody>
                        {items.map((a) => (
                            <tr key={a.id} data-testid={`adjustment-${a.id}`}>
                                <td className="text-xs">{a.kind === "deduction" ? "Deduction" : "Earning"}: {typeLabel(a)}</td>
                                <td className="text-xs">{a.label}</td>
                                <td className="text-xs font-mono">{a.period_start} to {a.period_end}</td>
                                <td className="num font-mono">{formatPHP(a.amount)}</td>
                                <td className="text-xs">{a.status}</td>
                                <td>{canEdit && a.status === "submitted" && <button className="h-7 px-2 text-xs rounded-md border border-border flex items-center gap-1" onClick={() => approve(a)} data-testid={`adjustment-approve-${a.id}`}><Check className="size-3" /> Approve</button>}</td>
                            </tr>
                        ))}
                    </tbody>
                </TableWrap>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Record a one-time deduction or earning</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <div><Label>Employee</Label><EmployeePicker value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} testId="adj-employee" /></div>
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label>Kind</Label>
                                <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value, adjustment_type: e.target.value === "earning" ? "bonus" : "uniform" })} data-testid="adj-kind">
                                    <option value="deduction">Deduction</option><option value="earning">Earning</option>
                                </select></div>
                            <div><Label>Type</Label>
                                <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={form.adjustment_type} onChange={(e) => setForm({ ...form, adjustment_type: e.target.value })} data-testid="adj-type">
                                    {types.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                </select></div>
                        </div>
                        <div><Label>Label</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Polo shirt, size M" data-testid="adj-label" /></div>
                        <div><Label>Amount (₱)</Label><Input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="adj-amount" /></div>
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label>Period from</Label><Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="adj-from" /></div>
                            <div><Label>Period to</Label><Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="adj-to" /></div>
                        </div>
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm disabled:opacity-60" disabled={busy} onClick={save} data-testid="adj-submit">Record</button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
